// The control overlay: seed + level inputs, regenerate / random-seed buttons, toggles,
// and a live stats + validation readout.

import type { LevelStats } from "../map/grammar";
import type { ValidationReport } from "../map/validate";
import type { LevelPlan } from "../config";
import { balance } from "../systems/balance";
import { music } from "../engine/music";

export interface PanelState {
  seed: string;
  level: number;
  curbs: boolean;
  grid: boolean;
  armies: boolean;
  tanks: boolean;
}

/** Format the level stats + validation readout (shared by the old panel and the DEV-menu Stats bank). */
export function formatStats(
  stats: LevelStats, report: ValidationReport, plan: LevelPlan, reached: boolean,
): { text: string; color: string } {
  const ok = report.ok;
  const b = stats.bbox;
  const w = Math.round(b.maxX - b.minX);
  const d = Math.round(b.maxZ - b.minZ);
  const lines = [
    `tiles     ${stats.tiles} / ${plan.targetTiles}${reached ? "" : "  (early)"}`,
    `corridors ${stats.corridors}   rooms ${stats.rooms}`,
    `turns     ${stats.corners}   ramps ${stats.ramps}   branches ${stats.branches}`,
    `arenas ${stats.arenas}   bridges ${stats.bridges}   chambers ${stats.chambers}`,
    `elevation ${stats.minElev}–${stats.maxElev}   (max ${plan.maxElev})`,
    `path      ${Math.round(stats.pathLength)} studs`,
    `footprint ${w} × ${d} studs`,
    "",
    `valid     ${ok ? "OK" : "FAIL"}`,
    `  solvable ${report.solvable ? "y" : "n"}   reachable ${report.reachableCells}/${report.cells}`,
    `  overlaps ${report.overlaps}   ramps ${report.rampsWellFormed ? "ok" : "bad"}`,
    `  curb-blocked slopes ${report.curbBlockedTransitions}`,
  ];
  if (report.errors.length) lines.push("  " + report.errors.join("\n  "));
  return { text: lines.join("\n"), color: ok ? "#9fe1cb" : "#f0997b" };
}

export class Panel {
  state: PanelState;
  onChange: (s: PanelState) => void = () => {};
  onSweep: () => void = () => {};

  private seedInput!: HTMLInputElement;
  private levelInput!: HTMLInputElement;
  private stats!: HTMLDivElement;
  private economy!: HTMLDivElement;

  constructor(container: HTMLElement, initial: PanelState) {
    this.state = { ...initial };
    this.build(container);
  }

  private build(container: HTMLElement): void {
    const root = document.createElement("div");
    Object.assign(root.style, {
      position: "fixed",
      left: "12px",
      top: "12px",
      width: "268px",
      padding: "12px 14px",
      background: "rgba(12,16,20,0.86)",
      border: "1px solid #2a3038",
      borderRadius: "8px",
      color: "#dfe3ea",
      font: "12px/1.5 'Courier New', monospace",
      userSelect: "none",
      maxHeight: "calc(100vh - 24px)",
      overflowY: "auto",
    } as CSSStyleDeclaration);

    root.appendChild(this.heading("longfight — map lab"));

    // seed row
    const seedRow = this.row();
    this.seedInput = document.createElement("input");
    this.seedInput.value = this.state.seed;
    this.styleInput(this.seedInput, "120px");
    this.seedInput.addEventListener("change", () => {
      this.state.seed = this.seedInput.value.trim() || "longfight";
      this.emit();
    });
    const rnd = this.button("random", () => {
      this.state.seed = Math.random().toString(36).slice(2, 8);
      this.seedInput.value = this.state.seed;
      this.emit();
    });
    seedRow.append(this.label("seed"), this.seedInput, rnd);
    root.appendChild(seedRow);

    // level row
    const lvlRow = this.row();
    this.levelInput = document.createElement("input");
    this.levelInput.type = "number";
    this.levelInput.min = "1";
    this.levelInput.value = String(this.state.level);
    this.styleInput(this.levelInput, "56px");
    this.levelInput.addEventListener("change", () => {
      this.state.level = Math.max(1, Math.floor(Number(this.levelInput.value) || 1));
      this.levelInput.value = String(this.state.level);
      this.emit();
    });
    const dec = this.button("−", () => this.bumpLevel(-1));
    const inc = this.button("+", () => this.bumpLevel(1));
    lvlRow.append(this.label("level"), dec, this.levelInput, inc);
    root.appendChild(lvlRow);

    // toggles
    const tog = this.row();
    tog.append(
      this.checkbox("curbs", this.state.curbs, (v) => {
        this.state.curbs = v;
        this.emit();
      }),
      this.checkbox("grid", this.state.grid, (v) => {
        this.state.grid = v;
        this.emit();
      }),
      this.checkbox("armies", this.state.armies, (v) => {
        this.state.armies = v;
        this.emit();
      }),
      this.checkbox("tanks", this.state.tanks, (v) => {
        this.state.tanks = v;
        this.emit();
      }),
    );
    root.appendChild(tog);

    // regenerate + sweep
    const regen = this.button("regenerate", () => this.emit());
    Object.assign(regen.style, { width: "100%", marginTop: "6px", padding: "6px" });
    root.appendChild(regen);
    const sweep = this.button("verify sweep (console)", () => this.onSweep());
    Object.assign(sweep.style, { width: "100%", marginTop: "6px", padding: "6px" });
    root.appendChild(sweep);

    // ---- live balance sliders (HP + damage-taken multipliers; lower dmg = tougher) ----
    const bh = document.createElement("div");
    Object.assign(bh.style, { marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #2a3038", color: "#8b93a0" });
    bh.textContent = "balance (live) · dmg× lower = tougher";
    root.appendChild(bh);
    root.appendChild(this.slider("soldier hp", 20, 500, 10, () => balance.soldierHp, (v) => (balance.soldierHp = v), (v) => String(Math.round(v))));
    root.appendChild(this.slider("giant hp", 100, 2000, 50, () => balance.giantHp, (v) => (balance.giantHp = v), (v) => String(Math.round(v))));
    root.appendChild(this.slider("giant blast dmg×", 0.05, 1, 0.05, () => balance.giantBlastDmg, (v) => (balance.giantBlastDmg = v), (v) => v.toFixed(2)));
    root.appendChild(this.slider("tank hp ×", 1, 12, 0.5, () => balance.tankHpMul, (v) => (balance.tankHpMul = v), (v) => v.toFixed(1)));
    root.appendChild(this.slider("tank bullet dmg×", 0.05, 1, 0.05, () => balance.tankBulletDmg, (v) => (balance.tankBulletDmg = v), (v) => v.toFixed(2)));
    root.appendChild(this.slider("tank blast dmg×", 0.05, 1, 0.05, () => balance.tankBlastDmg, (v) => (balance.tankBlastDmg = v), (v) => v.toFixed(2)));

    const rh = document.createElement("div");
    Object.assign(rh.style, { marginTop: "8px", color: "#8b93a0", fontSize: "11px" });
    rh.textContent = "rarity (how many appear)";
    root.appendChild(rh);
    root.appendChild(this.slider("tanks (max)", 0, 36, 1, () => balance.tankMax, (v) => (balance.tankMax = v), (v) => String(Math.round(v))));
    root.appendChild(this.slider("giants /team (max)", 0, 12, 1, () => balance.giantMax, (v) => (balance.giantMax = v), (v) => String(Math.round(v))));
    root.appendChild(this.slider("special troops", 0, 1, 0.05, () => balance.specialFrac, (v) => (balance.specialFrac = v), (v) => Math.round(v * 100) + "%"));

    // ---- Unit Points (UP) economy: plain grunts are free; everything else is bought from a banked
    // per-team budget, so specials stay rare filler amid the grunts. Higher regen = more specials. ----
    const eh = document.createElement("div");
    Object.assign(eh.style, { marginTop: "8px", color: "#8b93a0", fontSize: "11px" });
    eh.textContent = "unit points (grunts free, specials cost UP)";
    root.appendChild(eh);
    root.appendChild(this.slider("UP regen /s", 0, 60, 1, () => balance.upRegen, (v) => (balance.upRegen = v), (v) => String(Math.round(v))));
    this.economy = document.createElement("div");
    Object.assign(this.economy.style, { fontSize: "11px", color: "#9fe1cb", marginTop: "3px" });
    root.appendChild(this.economy);

    // ---- music (streaming shuffle of the tracker set) ----
    const mh = document.createElement("div");
    Object.assign(mh.style, { marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #2a3038", color: "#8b93a0" });
    mh.textContent = "music";
    root.appendChild(mh);
    root.appendChild(this.slider("volume", 0, 1, 0.05, () => music.getVolume(), (v) => music.setVolume(v), (v) => Math.round(v * 100) + "%"));
    const skip = this.button("next track ⏭", () => music.next());
    Object.assign(skip.style, { width: "100%", marginTop: "6px", padding: "6px" });
    root.appendChild(skip);

    // stats
    this.stats = document.createElement("div");
    Object.assign(this.stats.style, {
      marginTop: "10px",
      paddingTop: "10px",
      borderTop: "1px solid #2a3038",
      whiteSpace: "pre-wrap",
    } as CSSStyleDeclaration);
    root.appendChild(this.stats);

    // help
    const help = document.createElement("div");
    Object.assign(help.style, {
      marginTop: "10px",
      paddingTop: "8px",
      borderTop: "1px solid #2a3038",
      color: "#8b93a0",
    } as CSSStyleDeclaration);
    help.textContent =
      "drag: rotate · right-drag: pan\nscroll: zoom · WASD: pan\nF: fly (mouse-look, QE up/down)\nP: play (first-person) · Esc: exit\nM: mute";
    root.appendChild(help);

    container.appendChild(root);
  }

  private bumpLevel(d: number): void {
    this.state.level = Math.max(1, this.state.level + d);
    this.levelInput.value = String(this.state.level);
    this.emit();
  }

  private emit(): void {
    this.onChange({ ...this.state });
  }

  /** Live per-team banked Unit Points. Called every frame but the rounded integers change only a few
   *  times a second, so skip the textContent write (and its layout invalidation) when unchanged. */
  private ecoBlue = -1;
  private ecoRed = -1;
  setEconomy(blue: number, red: number): void {
    const b = Math.round(blue), r = Math.round(red);
    if (b === this.ecoBlue && r === this.ecoRed) return;
    this.ecoBlue = b; this.ecoRed = r;
    this.economy.textContent = `UP  blue ${b}   red ${r}`;
  }

  setStats(stats: LevelStats, report: ValidationReport, plan: LevelPlan, reached: boolean): void {
    const { text, color } = formatStats(stats, report, plan, reached);
    this.stats.textContent = text;
    this.stats.style.color = color;
  }

  // ---- tiny DOM helpers ----
  private heading(text: string): HTMLElement {
    const h = document.createElement("div");
    h.textContent = text;
    Object.assign(h.style, { fontWeight: "bold", marginBottom: "8px", color: "#fff" });
    return h;
  }
  private row(): HTMLDivElement {
    const r = document.createElement("div");
    Object.assign(r.style, { display: "flex", gap: "6px", alignItems: "center", marginBottom: "6px" });
    return r;
  }
  private label(text: string): HTMLSpanElement {
    const s = document.createElement("span");
    s.textContent = text;
    s.style.width = "42px";
    s.style.color = "#8b93a0";
    return s;
  }
  private styleInput(el: HTMLInputElement, width: string): void {
    Object.assign(el.style, {
      width,
      background: "#0c1116",
      border: "1px solid #2a3038",
      color: "#dfe3ea",
      font: "12px 'Courier New', monospace",
      padding: "4px 6px",
      borderRadius: "4px",
    } as CSSStyleDeclaration);
  }
  private button(text: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.textContent = text;
    Object.assign(b.style, {
      background: "#1a2029",
      border: "1px solid #2a3038",
      color: "#dfe3ea",
      font: "12px 'Courier New', monospace",
      padding: "4px 8px",
      borderRadius: "4px",
      cursor: "pointer",
    } as CSSStyleDeclaration);
    b.addEventListener("click", onClick);
    return b;
  }
  private checkbox(text: string, checked: boolean, onToggle: (v: boolean) => void): HTMLLabelElement {
    const l = document.createElement("label");
    Object.assign(l.style, { display: "flex", gap: "4px", alignItems: "center", cursor: "pointer" });
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = checked;
    cb.addEventListener("change", () => onToggle(cb.checked));
    l.append(cb, document.createTextNode(text));
    return l;
  }
  private slider(
    label: string, min: number, max: number, step: number,
    get: () => number, set: (v: number) => void, fmt: (v: number) => string,
  ): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.style.marginTop = "5px";
    const top = document.createElement("div");
    Object.assign(top.style, { display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#b8c0cc" });
    const lab = document.createElement("span");
    lab.textContent = label;
    const val = document.createElement("span");
    val.textContent = fmt(get());
    val.style.color = "#9fe1cb";
    top.append(lab, val);
    const input = document.createElement("input");
    input.type = "range";
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(get());
    Object.assign(input.style, { width: "100%", height: "14px", cursor: "pointer", accentColor: "#3a78ff" });
    input.addEventListener("input", () => {
      const v = parseFloat(input.value);
      set(v);
      val.textContent = fmt(v);
    });
    wrap.append(top, input);
    return wrap;
  }
}
